import BigNumber from "bignumber.js";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sheet } from "react-modal-sheet";
import { useLocation, useNavigate } from "react-router-dom";
import {
  KeyboardDecimal,
  KeyboardProvider,
  useKeyboard,
} from "@/components/keyboard";
import Spinner from "@/components/spinner";
import { useBalanceWLD } from "@/hooks/useBalanceWld";
import { useBuyChest } from "@/hooks/useBuyChest";
import { useSignIn } from "@/hooks/useSignIn";
import { useWalletAddress } from "@/hooks/useWalletAddress";
import {
  getPaymentPreview,
  type PaymentPreview,
} from "@/stores/preview-buy-chest";
import { mergeAPIError } from "@/utils/errors";
import { CONSTANTS } from "@/constants";

const suggestAmount = [2, 5, 10, 15, 20];

const useDebouncedPreview = (quantity: string | number) => {
  const [previewData, setPreviewData] = useState<PaymentPreview | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const controllerRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Validate input
    if (!quantity || quantity === "") {
      setPreviewData(undefined);
      setError(undefined);
      return;
    }

    const num = Number(quantity);
    if (num <= 0 || !Number.isInteger(num) || isNaN(num)) {
      setPreviewData(undefined);
      return;
    }

    // Cleanup previous request và timer
    const cleanup = () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };

    // Debounce request
    debounceRef.current = setTimeout(() => {
      // Cleanup trước khi tạo request mới
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      setLoading(true);
      setError(undefined);

      controllerRef.current = new AbortController();

      getPaymentPreview(Number(quantity), controllerRef.current.signal)
        .then((data) => {
          // Chỉ update nếu request chưa bị abort
          if (controllerRef.current) {
            setPreviewData(data);
            setError(undefined);
          }
        })
        .catch((err) => {
          // Chỉ xử lý lỗi nếu không phải abort
          if (
            err.name !== "AbortError" &&
            !err.message?.includes("aborted") &&
            !err.message?.includes("Fetch is aborted")
          ) {
            console.error("❌ Lỗi fetch preview:", err);
            const msg = mergeAPIError(err);
            setError(msg || "Failed to fetch preview");
            setPreviewData(undefined);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }, 500);

    // Cleanup function
    return cleanup;
  }, [quantity]);

  return { previewData, loading, error };
};

export function BuyChest() {
  const { t } = useTranslation();
  const address = useWalletAddress();
  const navigate = useNavigate();
  const location = useLocation();

  const backUrl = useMemo(() => {
    const parent = location.pathname.substring(
      0,
      location.pathname.lastIndexOf("/")
    );

    return parent || `/${CONSTANTS.BASE_PATH}`;
  }, [location?.pathname]);

  const { signIn, isLoading: isLoadingSignIn } = useSignIn();
  const { buy, isLoading: isLoadingBuy } = useBuyChest();
  const [quantity, setQuantity] = useState<string>("1");

  const {
    previewData,
    loading,
    error: errorResponse,
  } = useDebouncedPreview(quantity);

  const { data: balanceWLD, isLoading, mutate } = useBalanceWLD(address);

  const balanceWld = useMemo(
    () => new BigNumber(String(balanceWLD || 0)).dividedBy(1e18),
    [balanceWLD]
  );

  const error = useMemo(() => {
    let err = errorResponse;
    const amount = Number(quantity);
    if (
      !quantity ||
      amount <= 0 ||
      !Number.isInteger(amount) ||
      isNaN(amount)
    ) {
      err = "Invalid number";
    }

    const isInsufficient = balanceWld.lt(
      new BigNumber(previewData?.estimatedCost ?? 0)
    );
    if (isInsufficient) {
      err = "Insufficient Balance";
    }

    return err;
  }, [errorResponse, previewData?.estimatedCost, quantity, balanceWld]);

  const disabled = useMemo(() => {
    return isLoading || isLoadingSignIn || isLoadingBuy;
  }, [isLoading, isLoadingSignIn, isLoadingBuy]);

  const handleClose = useCallback(() => {
    navigate(backUrl, { replace: true });
  }, [navigate, backUrl]);

  return (
    <Sheet
      isOpen={true}
      detent="content"
      onClose={() => {
        handleClose();
      }}
    >
      <Sheet.Container
        style={{
          background: "#22170E",
        }}
      >
        <Sheet.Header />
        <Sheet.Content>
          <KeyboardProvider>
            <div className="h-full flex flex-col items-start justify-start px-4 w-full  bg-[#22170E] rounded-t-3xl">
              <p className="text-xl text-white text-center w-full ">
                {t("smashes_buy")}
              </p>

              <Suggest
                disabled={disabled}
                setQuantity={(v) => {
                  setQuantity(v);
                }}
              />

              <InputAmount
                quantity={quantity}
                setQuantity={setQuantity}
                disabled={disabled}
              />

              <div className="flex items-center justify-center gap-6 w-full min-h-6">
                {previewData && quantity && Number(quantity) > 0 ? (
                  <>
                    {previewData.minQuantityToDiscount <= Number(quantity) ? (
                      <p className=" text-center text-base opacity-50 text-white line-through">
                        {BigNumber(previewData.price)
                          .multipliedBy(quantity)
                          .toNumber()}{" "}
                        WLD
                      </p>
                    ) : null}
                    <p
                      className="text-center text-base opacity-50 text-white"
                      style={{
                        letterSpacing: "-0.32px",
                      }}
                    >
                      {`${previewData.estimatedCost} WLD`}
                    </p>
                  </>
                ) : null}
                {(!error && loading) || isLoading ? (
                  <svg
                    className="animate-spin  h-3 w-3 text-white absolute z-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : null}
                {error && error !== "Insufficient Balance" ? (
                  <p className="h-4 italic text-red-400 text-center w-full">
                    {error}
                  </p>
                ) : null}
              </div>
              <p className="linear-text w-full pt-4 text-base text-center ani-shine">
                {t("discount")}
              </p>

              <div className="w-full pt-4">
                <KeyboardDecimal
                  maxDecimal={2}
                  maxLength={21}
                  type="number"
                  disabled={disabled}
                />
              </div>
              {/* <div className="flex-1"></div> */}
              <div className="w-full pb-6 pt-4">
                {!address ? (
                  <button
                    type="button"
                    disabled={disabled}
                    className={clsx(
                      "text-base flex text-black z-10 px-8 py-4 bg-gradient-to-l to-[#FEC537] from-[#F68703] transform active:scale-95 transition duration-300 rounded-full w-full text-center items-center justify-center  cursor-pointer",
                      { "opacity-50": isLoadingSignIn }
                    )}
                    onClick={() => {
                      signIn().finally(() => {
                        mutate();
                      });
                    }}
                  >
                    {t("login")}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={disabled}
                    className={clsx(
                      "text-base flex text-black z-10 px-8 py-4 bg-gradient-to-l to-[#FEC537] from-[#F68703] transform active:scale-95 transition duration-300 rounded-full w-full text-center items-center justify-center  cursor-pointer",
                      { "opacity-50": error || loading || isLoadingBuy }
                    )}
                    onClick={() => {
                      if (error || loading || isLoadingBuy) {
                        return;
                      }

                      buy(address, Number(quantity));
                      handleClose();
                    }}
                  >
                    {isLoadingBuy ? (
                      <Spinner />
                    ) : error === "Insufficient Balance" ? (
                      t("insufficient_balance")
                    ) : (
                      t("continue")
                    )}
                  </button>
                )}
              </div>
            </div>
          </KeyboardProvider>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop
        onTap={() => {
          handleClose();
        }}
      />
    </Sheet>
  );
}

function InputAmount(props: {
  quantity: any;
  setQuantity: (val: string) => void;
  disabled?: boolean;
}) {
  const { quantity, setQuantity, disabled = false } = props;
  const [showCursor, setShowCursor] = useState(false);
  const { openKeyboard, updateValue } = useKeyboard();

  const handleUpdateValue = useCallback(
    (newVal: string) => {
      setQuantity(newVal);
    },
    [setQuantity]
  );

  const handleClick = useCallback(
    (initValue: string) => {
      if (showCursor) {
        updateValue(initValue);
        return;
      }
      setShowCursor(true);
      openKeyboard(initValue, handleUpdateValue);
    },
    [handleUpdateValue, openKeyboard, quantity, showCursor]
  );

  useEffect(() => {
    if (showCursor) {
      return;
    }

    // setTimeout(() => {
    //   handleClick();
    // }, 200);
  }, [handleClick, showCursor]);

  return (
    <div className="flex flex-row gap-2 items-center justify-between pt-2 relative w-full px-12 min-h-14">
      <button
        className="flex-none w-10 h-10 z-10 rounded-full flex items-center justify-center border border-[#71563F] absolute left-0 top-4"
        type="button"
        disabled={disabled}
        onClick={() => {
          let num = Number(quantity || 0) - 1;
          if (num < 0) {
            num = 0;
          }
          handleClick(String(num));
          // updateValue();
          // setQuantity(String(num));
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="24"
          viewBox="0 0 25 24"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.5 12C3.5 11.7348 3.60536 11.4804 3.79289 11.2929C3.98043 11.1054 4.23478 11 4.5 11H20.5C20.7652 11 21.0196 11.1054 21.2071 11.2929C21.3946 11.4804 21.5 11.7348 21.5 12C21.5 12.2652 21.3946 12.5196 21.2071 12.7071C21.0196 12.8946 20.7652 13 20.5 13H4.5C4.23478 13 3.98043 12.8946 3.79289 12.7071C3.60536 12.5196 3.5 12.2652 3.5 12Z"
            fill="#71563F"
          />
        </svg>
      </button>

      <div
        className="flex items-center justify-center gap-1 self-stretch w-full"
        onClick={() => {
          handleClick(quantity);
        }}
      >
        <span className="flex items-center justify-center relative gap-1 pointer-events-none min-h-9 text-[40px] leading-[48px] text-center text-[#D1D4DC] ">
          <span>{props.quantity ?? " "}</span>
          {!quantity || quantity.length === 0 ? (
            <span className="opacity-45">0</span>
          ) : null}
          <span className="cursor h-[36px] w-[3px] bg-[#1966F7] ani-cursor  pointer-events-none"></span>
        </span>
      </div>

      <button
        className="flex-none w-10 h-10 rounded-full flex items-center justify-center border border-[#71563F] absolute right-0 top-4"
        type="button"
        disabled={disabled}
        onClick={() => {
          const num = Number(quantity || 0) + 1;

          handleClick(String(num));

          // setQuantity(String(num));
          // updateValue(String(num));
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="24"
          viewBox="0 0 25 24"
          fill="none"
        >
          <path
            d="M11.5 20C11.5 20.2652 11.6054 20.5196 11.7929 20.7071C11.9804 20.8946 12.2348 21 12.5 21C12.7652 21 13.0196 20.8946 13.2071 20.7071C13.3946 20.5196 13.5 20.2652 13.5 20V13H20.5C20.7652 13 21.0196 12.8946 21.2071 12.7071C21.3946 12.5196 21.5 12.2652 21.5 12C21.5 11.7348 21.3946 11.4804 21.2071 11.2929C21.0196 11.1054 20.7652 11 20.5 11H13.5V4C13.5 3.73478 13.3946 3.48043 13.2071 3.29289C13.0196 3.10536 12.7652 3 12.5 3C12.2348 3 11.9804 3.10536 11.7929 3.29289C11.6054 3.48043 11.5 3.73478 11.5 4V11H4.5C4.23478 11 3.98043 11.1054 3.79289 11.2929C3.60536 11.4804 3.5 11.7348 3.5 12C3.5 12.2652 3.60536 12.5196 3.79289 12.7071C3.98043 12.8946 4.23478 13 4.5 13H11.5V20Z"
            fill="#71563F"
          />
        </svg>
      </button>
    </div>
  );
}

function Suggest(props: {
  disabled: boolean;
  setQuantity: (val: string) => void;
}) {
  const { disabled, setQuantity } = props;
  const { updateValue } = useKeyboard();

  return (
    <div className="flex items-center gap-2 mx-auto mt-2">
      {suggestAmount.map((value) => {
        return (
          <button
            key={`suggest-${value}`}
            type="button"
            disabled={disabled}
            onClick={() => {
              updateValue(String(value));
              setQuantity(String(value));
              //   handleLoadFace();
            }}
            className={clsx(
              "border border-[#d79a47] opacity-80",
              "min-w-[48px] rounded-lg text-[#d79a47]"
            )}
          >
            {value}
          </button>
        );
      })}
    </div>
  );
}
